import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";

import { useListBrands } from "../../../api/generated/endpoints/teams/teams";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import { SoonControl } from "../Soon";
import classes from "./generations.module.css";
import { GenerationsGallery, type Kind } from "./GenerationsGallery";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/pro/generations";
const GENERATIONS = "/teams/$teamId/brands/$brandId/pro/generations";

/**
 * The prototype's chips, in its order: each the read an existing page makes (decision 27; the
 * gallery's own), or `Soon` where the API has no filter for it.
 */
const KINDS: readonly ({ label: string; kind: Kind | undefined } | { label: string; soon: true })[] = [
  { label: "All", kind: undefined },
  { label: "Images", kind: "image" },
  { label: "Ad statics", kind: "ad-static" },
  { label: "Videos", kind: "video" },
  // The multi-shot storyboard is not built (G16, #981).
  { label: "Storyboard ads", soon: true },
  { label: "Audio", kind: "audio" },
  // The list filters by `active` or `all` alone: no failed-only read yet.
  { label: "Failed", soon: true },
];

/**
 * The prototype's `#generations`: everything the brand made, the user's or the team's, in days;
 * a result opens the Studio's own detail (spec ui-perf/pro-studio-redesign, PR d1).
 */
export function GenerationsPage() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  // The brand menu keeps the page mounted: another brand starts anew, with no detail of the last
  // brand's open and Mine again.
  return <BrandGenerations key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />;
}

/** One brand's results, Mine or Team, and the detail of the one opened: the gallery, on Pro's URL. */
function BrandGenerations({ teamId, brandId }: { teamId: string; brandId: string }) {
  const { kind, job: linked } = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: "/teams/$teamId/brands/$brandId/pro/generations" });
  const brand = useListBrands(teamId).data?.find((each) => each.id === brandId);
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Generations</h1>
          <p className={page.lede}>
            Everything you and your team made for {brand?.name ?? "this brand"}. Results save here
            on their own.
          </p>
        </div>
      </div>
      <GenerationsGallery
        teamId={teamId}
        brandId={brandId}
        kind={kind}
        linked={linked}
        onCloseLinked={() => void navigate({ search: { kind }, replace: true })}
        kinds={
          <nav className={classes.kinds} aria-label="Kinds">
            {KINDS.map((each) =>
              "soon" in each ? (
                <SoonControl key={each.label} className={classes.kind}>
                  {each.label}
                </SoonControl>
              ) : (
                <Link
                  key={each.label}
                  to={GENERATIONS}
                  params={{ teamId, brandId }}
                  search={{ kind: each.kind }}
                  className={classes.kind}
                  aria-current={each.kind === kind ? "page" : undefined}
                  activeOptions={{ exact: true, includeSearch: true }}
                >
                  {each.label}
                </Link>
              ),
            )}
          </nav>
        }
      />
    </div>
  );
}
